/**
 * Super Admin Login Page - 슈퍼관리자 전용 로그인 페이지
 * 
 * 일반 Firebase Auth와 완전히 분리된 독립적인 관리자 계정 시스템
 * Firestore /admin/super_admins/accounts 컬렉션의 계정으로 로그인
 */

import { Trans, t } from '@lingui/macro';
import { Loader2, Shield, AlertCircle } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { getFunc } from '../../services/FireClient';
import { SuperAdmin } from '../../services/SuperAdmin';
import { AdminFire } from '../../services/SuperAdminFirebase';

interface SuperAdminLoginResponse {
    success: boolean;
    token: string;
    role: 'superAdmin' | 'developer';
    admin: {
        id: string;
        email: string;
        name: string;
    };
}

const SuperAdminLoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [inProgress, setInProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        // 이미 로그인되어 있고 슈퍼관리자인 경우 바로 이동
        if (SuperAdmin.isLoggedIn) {
            navigate('/super-admin', { replace: true });
        }
    }, [navigate]);

    const handleLogin = async () => {
        if (!email || !password) {
            setErrorMessage(t`Please enter email and password`);
            return;
        }

        setInProgress(true);
        setErrorMessage('');

        try {
            // 슈퍼관리자 전용 로그인 API 호출
            const superAdminLogin = getFunc('superAdminLogin');
            const result = await superAdminLogin({ email, password });
            const data = result.data as SuperAdminLoginResponse;

            if (data.success && data.token) {
                // 슈퍼관리자 상태 설정
                SuperAdmin.login(data.admin);

                // 별도 Firebase 인스턴스로 로그인 (자격증명도 저장하여 자동 갱신 활성화)
                await AdminFire.signIn(data.token, email, password);

                console.log('Admin logged in:', data.admin.name, 'role:', data.role);

                // role에 따라 다른 페이지로 이동
                if (data.role === 'developer') {
                    window.location.href = '/dev-admin';
                } else {
                    window.location.href = '/super-admin';
                }
            } else {
                setErrorMessage(t`Login failed. Please check your credentials.`);
            }
        } catch (error: any) {
            console.error('Super admin login error:', error);

            // Firebase Functions 에러 처리
            const errorCode = error?.code || '';
            const errorMsg = error?.message || '';

            if (errorCode.includes('not-found') || errorMsg.includes('not-found')) {
                setErrorMessage(t`Account not found.`);
            } else if (errorCode.includes('unauthenticated') || errorMsg.includes('unauthenticated')) {
                setErrorMessage(t`Invalid credentials.`);
            } else {
                setErrorMessage(t`Login failed. Please check your credentials.`);
            }
        }

        setInProgress(false);
    };

    return (
        <div className="min-h-screen w-full bg-slate-900 flex items-center justify-center p-4">
            <Card className="w-full max-w-md bg-slate-800 border-slate-700 shadow-2xl">
                <CardHeader className="text-center space-y-4">
                    <div className="mx-auto w-16 h-16 rounded-full bg-indigo-600/20 flex items-center justify-center">
                        <Shield className="w-8 h-8 text-indigo-400" />
                    </div>
                    <div>
                        <CardTitle className="text-2xl text-white">
                            <Trans>Super Admin</Trans>
                        </CardTitle>
                        <CardDescription className="text-slate-400">
                            <Trans>Authorized personnel only</Trans>
                        </CardDescription>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Error Message */}
                    {errorMessage && (
                        <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-lg">
                            <AlertCircle className="w-4 h-4 shrink-0" />
                            <span>{errorMessage}</span>
                        </div>
                    )}

                    {/* Email */}
                    <div className="space-y-2">
                        <Label className="text-slate-300"><Trans>Email</Trans></Label>
                        <Input
                            type="email"
                            placeholder="superadmin@nextpod.jp"
                            value={email}
                            onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                            disabled={inProgress}
                            className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                        />
                    </div>

                    {/* Password */}
                    <div className="space-y-2">
                        <Label className="text-slate-300"><Trans>Password</Trans></Label>
                        <Input
                            type="password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={inProgress}
                            className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                        />
                    </div>

                    {/* Login Button */}
                    <Button
                        className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
                        onClick={handleLogin}
                        disabled={inProgress}
                    >
                        {inProgress ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                <Trans>Authenticating...</Trans>
                            </>
                        ) : (
                            <Trans>Sign In</Trans>
                        )}
                    </Button>

                    {/* Info */}
                    <div className="text-center text-xs text-slate-500 pt-2">
                        <p><Trans>This is a separate admin system.</Trans></p>
                        <p><Trans>Contact system administrator for access.</Trans></p>
                    </div>

                    {/* Back to normal login */}
                    <div className="text-center pt-4 border-t border-slate-700">
                        <button
                            onClick={() => navigate('/login')}
                            className="text-sm text-slate-500 hover:text-slate-400 transition-colors"
                        >
                            <Trans>← Back to normal login</Trans>
                        </button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default SuperAdminLoginPage;
